import React, { useEffect, useId, useRef, useState } from "react";

interface MetricBreakdownPopoverProps {
  /** Popover heading — the metric name. */
  title: string;
  /** Whether there is anything to drill into (no popover when false). */
  hasContent: boolean;
  /** Popover body (typically a small list of customers). */
  content: React.ReactNode;
  /** Optional id of an element describing the metric state; set as `aria-describedby` on the trigger. */
  describedById?: string;
  /** The visible trigger — the metric value. */
  children: React.ReactNode;
}

/**
 * Lightweight, dependency-free popover used to drill into a metric and reveal the
 * customers behind the number. Opens on hover, keyboard focus, AND tap (click) for
 * touch devices; closes on mouse-leave, Escape, click-outside, or when focus leaves
 * the whole subtree. Mouse-leave lives on the wrapping element so moving the pointer
 * from the value into the popover (a child) keeps it open. Blur only closes when
 * focus leaves the subtree entirely, so the drill-down list can be tabbed through.
 *
 * When `hasContent` is false the trigger renders plain (nothing to reveal).
 */
const MetricBreakdownPopover: React.FC<MetricBreakdownPopoverProps> = ({
  title,
  hasContent,
  content,
  describedById,
  children,
}) => {
  const [open, setOpen] = useState(false);
  const popoverId = useId();
  const wrapperRef = useRef<HTMLSpanElement>(null);

  // Close on click/tap outside the wrapping element (touch + mouse + pen).
  useEffect(() => {
    if (!open) {
      return;
    }
    const handlePointerDown = (e: PointerEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node | null)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open]);

  if (!hasContent) {
    return <>{children}</>;
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <span
      ref={wrapperRef}
      className="relative inline-flex"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setOpen(false);
        }
      }}
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        aria-describedby={describedById}
        onClick={() => setOpen((o) => !o)}
        className="cursor-pointer border-b border-dotted border-neutral-400 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      >
        {children}
      </button>
      {open && (
        <div
          id={popoverId}
          role="region"
          aria-label={title}
          className="absolute left-0 top-full z-20 mt-1 w-80 rounded-lg border border-neutral-200 bg-white text-left shadow-lg"
        >
          {/* Relative wrapper provides a clipping context for the scroll-fade gradient. */}
          <div className="relative max-h-72 overflow-auto overscroll-contain p-3">
            <div className="mb-2 text-xs font-semibold text-neutral-700">
              {title}
            </div>
            {content}
            {/* Visual scroll affordance — gradient fades the bottom edge when content overflows. */}
            <div
              aria-hidden="true"
              className="pointer-events-none sticky bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white to-transparent"
            />
          </div>
        </div>
      )}
    </span>
  );
};

export default MetricBreakdownPopover;
